import { CompanyAnnualRevenue } from '@aibees/elephant/components/company-annual-revenue';
import { CompanyProfile } from '@aibees/elephant/components/company-profile';
import { LabelActionCell } from '@aibees/elephant/components/label-action';
import { SearchDataNotFound } from '@aibees/elephant/components/search-data-not-found';
import { TableLoadingSpinner } from '@aibees/elephant/components/table-loading-spinner';
import { CompanySave } from '@aibees/elephant/feature/search-table-company/components/company-save';
import { useBrowseSearchIds } from '@aibees/elephant/hooks/use-browse-search-ids';
import { ElephantPresetType } from '@aibees/elephant/types';
import { LabelType } from '@aibees/elephant/types/label';
import { formatCompanyLocation } from '@aibees/elephant/utils';
import {
  TableCellTypography,
  TableColumnMetadata,
} from '@aibees/shared/components';
import { coloredIcons } from '@aibees/shared/constants/icons';
import { AdvanceTableConfig } from '@aibees/shared/features';
import { capitalize } from '@aibees/shared/utils';
import { LookalikeButtonWithSharedData } from './components/lookalike-button/lookalike-button-with-shared-data';
import { SearchTableHeaderWithContext } from './components/search-table-header/search-table-header-with-context';
import { TableDataViewSwitcher } from './components/table-data-view-switcher';
import { useBrowseCompanySearchForTable } from './hooks/use-browse-company-search-for-table';
import * as SX from './sx';
import { CompanyTableInfo } from './types';

export const COMPANY_SEARCH_API_RESPONSE_PERF_MARK =
  'company-search-api-response';

const companyMetadata: TableColumnMetadata = {
  ...coloredIcons.company,
  type: 'Company',
};

/** Stable empty array for label ids to avoid new reference on every render */
const EMPTY_LABEL_IDS: string[] = [];

const COMPANY_PROFILE_NAME_PROPS = {
  variant: 'body2' as const,
  fontWeight: 600,
};

export const config: AdvanceTableConfig<CompanyTableInfo> = {
  rowKey: 'id',
  cellSx: { p: '8px 12px' },
  rowSelectable: true,
  showSelectAll: true,
  paidBulkSelection: true,
  paidPagination: true,
  columnModifierKey: 'company-search-table',
  loadingText: 'Loading...',
  loadingComponent: <TableLoadingSpinner />,
  columnModifierPresetType: ElephantPresetType.COMPANY_SEARCH_TABLE_LAYOUT,
  exportSettingPresetType:
    ElephantPresetType.COMPANY_SEARCH_EXPORT_SETTINGS_COLUMNS,
  columns: [
    {
      key: 'name',
      label: 'Company',
      size: 'large', // 312px
      render: (_, v) => (
        <CompanyProfile
          name={v.summary?.name}
          imageSrc={v.summary?.logo?.source || ''}
          id={v.id}
          socialLink={v.link}
          showPopover
          avatarSize={40}
          nameProps={COMPANY_PROFILE_NAME_PROPS}
        />
      ),
      disabledInColumnModifier: true,
      isSticky: true,
      metadata: companyMetadata,
    },
    {
      key: 'lookalike',
      label: 'Lookalike',
      render: (_, v) => (
        <LookalikeButtonWithSharedData currentItem={v} fullWidth />
      ),
      disabledInColumnModifier: true,
      size: 'small', // 152px
      metadata: {
        type: 'Company',
        icon: 'fingerPrint',
        color: companyMetadata.color,
      },
    },
    {
      key: 'actions',
      label: 'Quick Actions',
      disabledInColumnModifier: true,
      align: 'left',
      size: 'small', // 152px
      render: (_, v) => <CompanySave item={v} fullWidth />,
      metadata: companyMetadata,
    },
    {
      key: 'size',
      label: 'Headcount',
      size: 'small', // 152px
      metadata: companyMetadata,
      render: (_, v) => (
        <TableCellTypography
          item={v.summary?.staff?.total?.toLocaleString() ?? ''}
        />
      ),
    },
    {
      id: 'location',
      size: 'medium', // 184px
      label: 'Company Location',
      metadata: companyMetadata,
      render: (_, v) => (
        <TableCellTypography
          item={
            capitalize(
              formatCompanyLocation([
                v.location?.headquarter?.country,
                v.location?.headquarter?.state,
                v.location?.headquarter?.city,
              ])
            ) || ''
          }
        />
      ),
    },
    {
      key: 'industry',
      label: 'Primary Industry',
      size: 'medium', // 184px
      metadata: companyMetadata,
      render: (_, v) => (
        <TableCellTypography hasCapitalize item={v.summary?.industry || ''} />
      ),
    },
    {
      key: 'industryTags',
      label: 'Industry Tags',
      size: 'medium', // 184px
      metadata: companyMetadata,
      render: (_, v) => (
        <TableCellTypography item={capitalize(v.industries?.join(', '))} />
      ),
    },
    {
      key: 'product-and-services',
      label: 'Product and Services',
      size: 'medium', // 184px
      metadata: companyMetadata,
      render: (_, v) => (
        <TableCellTypography
          item={capitalize(v.keywords?.filter(Boolean)?.join(', ') ?? '')}
        />
      ),
    },
    {
      key: 'company-type',
      label: 'Company Type ',
      size: 'medium', // 184px
      metadata: companyMetadata,
      render: (_, v) => (
        <TableCellTypography item={capitalize(v.summary?.type)} />
      ),
    },

    {
      key: 'account-lists',
      label: 'Account Lists',
      size: 'large', // 312px
      metadata: companyMetadata,
      render: (_, v) => (
        <LabelActionCell
          id={v.id}
          type={LabelType.LIST}
          currentLabelIds={v.labels ?? EMPTY_LABEL_IDS}
        />
      ),
    },
    {
      key: 'account-labels',
      label: 'Account Labels',
      size: 'large', // 184px
      metadata: companyMetadata,
      render: (_, v) => (
        <LabelActionCell
          id={v.id}
          type={LabelType.LABEL}
          currentLabelIds={v.labels ?? EMPTY_LABEL_IDS}
        />
      ),
    },
    {
      id: 'financial',
      key: 'financial.revenue',
      label: 'Company Revenue',
      size: 'medium', // 184px
      metadata: companyMetadata,
      render: (_, v) => (
        <CompanyAnnualRevenue value={v.financial?.revenue?.annual?.amount} />
      ),
    },
  ],

  actions: [],
  filters: [],
  applyStateToQueryParams: true,
  browseApi: useBrowseCompanySearchForTable,
  browseIdsApi: useBrowseSearchIds,
  apiResponsePerformanceMark: COMPANY_SEARCH_API_RESPONSE_PERF_MARK,
  renderTablePrependStable: (_slot) => (
    <SearchTableHeaderWithContext sx={SX.buttonGroup}>
      <TableDataViewSwitcher />
    </SearchTableHeaderWithContext>
  ),
  sx: { gap: 0.5 },
  hideColumnsOnNoData: true,
  emptyProps: SearchDataNotFound,
  rowsPerPageOptions: [25],
  pageSelectorMax: 100,
  maxStickyRows: 5,
  // maxHeight: 'calc(100vh - 312px)',
  manualSelectionLimit: 100,
  includeStickyRowsInSelection: true,
  bordered: 'solid',
  stickyHeader: true,
  showJumpToPage: true,
  prependSortingFields: { employee_count: 'desc' },
  enableUpdatedSearchItems: true,
};
